import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RoomCard from "@/components/RoomCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Search, Filter, Lock, Plus, ArrowRight, MapPin, Sparkles } from "lucide-react";
import PullToRefresh from "@/components/mobile/PullToRefresh";

const FREE_LIMIT = 6;

export default function Browse() {
    const { user } = useAuth();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        city: "", country: "", min_price: "", max_price: "",
        available_from: "", available_to: "",
    });

    const load = async (f = filters) => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
            const { data } = await api.get("/rooms", { params });
            setRooms(data);
        } catch (err) {
            console.warn("rooms load failed", err?.message);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    // Reload the catalog when the app returns to the foreground so users
    // don't get stuck looking at a stale "loading" grid after reopening.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") load(filters);
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const apply = (e) => { e.preventDefault(); load(filters); };
    const reset = () => {
        const blank = { city: "", country: "", min_price: "", max_price: "", available_from: "", available_to: "" };
        setFilters(blank);
        load(blank);
    };
    const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

    // Is any filter active? (used to decide what to render in locked state)
    const hasActiveFilter = Object.values(filters).some((v) => v !== "" && v !== null);

    // Gating logic:
    // - Logged-in + has listed a room -> see all rooms + filters work normally.
    // - Admins are always unlocked (so they can moderate without listing a room).
    // - Otherwise -> see first FREE_LIMIT freely; filtering is locked behind the same CTA.
    const isUnlocked = !!(user && (user.is_admin || user.has_listed_room));
    const showLockedOnly = !isUnlocked && hasActiveFilter;
    const visibleRooms = isUnlocked
        ? rooms
        : showLockedOnly
            ? []
            : rooms.slice(0, FREE_LIMIT);
    const showLockedSection = !isUnlocked && (showLockedOnly || rooms.length > FREE_LIMIT);
    const teaserRooms = isUnlocked ? [] : rooms.slice(FREE_LIMIT, FREE_LIMIT + 3);

    return (
        <PullToRefresh onRefresh={() => load(filters)}>
        <div data-testid="browse-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
            {/* Mobile-first hero: vibrant gradient card that doubles as the filter trigger */}
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="md:hidden relative overflow-hidden rounded-3xl p-6 text-white"
                style={{ background: "linear-gradient(135deg, #dd6b4d 0%, #e59374 45%, #f4c86b 100%)" }}
            >
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] opacity-80">Discover</div>
                <h1 className="font-display text-3xl font-bold mt-2 leading-tight">Rooms worldwide</h1>
                <p className="text-white/90 mt-2 text-sm max-w-[280px]">Swap your room with someone heading the other way.</p>

                <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                    <SheetTrigger asChild>
                        <button data-testid="mobile-filter-trigger" className="mt-5 w-full bg-white/95 hover:bg-white text-foreground rounded-full h-12 px-5 flex items-center gap-3 active:scale-98 transition-transform shadow-lg">
                            <Search className="w-5 h-5 text-primary" />
                            <span className="text-sm font-medium text-muted-foreground flex-1 text-left truncate">
                                {filters.city ? `${filters.city}${filters.min_price || filters.max_price ? " · price" : ""}${filters.available_from ? " · dates" : ""}` : "Search cities, dates, price…"}
                            </span>
                            {hasActiveFilter && <span className="w-2 h-2 rounded-full bg-primary" />}
                        </button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="rounded-t-[28px] max-h-[85vh] overflow-y-auto">
                        <SheetTitle>Filter rooms</SheetTitle>
                        <SheetDescription className="sr-only">Filter the room list by city, price range and date window.</SheetDescription>
                        <form onSubmit={(e) => { apply(e); setFilterOpen(false); }} className="mt-4 space-y-3">
                            <div>
                                <Label>City</Label>
                                <Input data-testid="filter-city-input" className="mt-1 rounded-xl h-11" value={filters.city} onChange={set("city")} placeholder="e.g. Lisbon" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Min price</Label>
                                    <Input type="number" data-testid="filter-min-price-input" className="mt-1 rounded-xl h-11" value={filters.min_price} onChange={set("min_price")} placeholder="0" />
                                </div>
                                <div>
                                    <Label>Max price</Label>
                                    <Input type="number" data-testid="filter-max-price-input" className="mt-1 rounded-xl h-11" value={filters.max_price} onChange={set("max_price")} placeholder="∞" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>From</Label>
                                    <Input type="date" data-testid="filter-from-input" className="mt-1 rounded-xl h-11" value={filters.available_from} onChange={set("available_from")} />
                                </div>
                                <div>
                                    <Label>Until</Label>
                                    <Input type="date" data-testid="filter-to-input" className="mt-1 rounded-xl h-11" value={filters.available_to} onChange={set("available_to")} />
                                </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button type="button" variant="outline" onClick={() => { reset(); setFilterOpen(false); }} data-testid="filter-reset-btn" className="flex-1 rounded-full h-11">Reset</Button>
                                <Button type="submit" data-testid="filter-apply-btn" className="flex-1 rounded-full h-11 bg-primary hover:bg-primary/90">
                                    <Search className="w-4 h-4 mr-1" /> Search
                                </Button>
                            </div>
                        </form>
                    </SheetContent>
                </Sheet>
            </motion.div>

            {/* Desktop hero (unchanged) */}
            <div className="hidden md:flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Browse</div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">Rooms worldwide</h1>
                </div>
            </div>

            {/* Desktop filter form (unchanged) */}
            <form onSubmit={apply} className="hidden md:grid bg-white border border-border/60 rounded-2xl p-5 mt-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                <div className="lg:col-span-2">
                    <Label>City</Label>
                    <Input data-testid="filter-city-input" className="mt-1 rounded-xl h-10" value={filters.city} onChange={set("city")} placeholder="e.g. Lisbon" />
                </div>
                <div>
                    <Label>Min price</Label>
                    <Input type="number" data-testid="filter-min-price-input" className="mt-1 rounded-xl h-10" value={filters.min_price} onChange={set("min_price")} placeholder="0" />
                </div>
                <div>
                    <Label>Max price</Label>
                    <Input type="number" data-testid="filter-max-price-input" className="mt-1 rounded-xl h-10" value={filters.max_price} onChange={set("max_price")} placeholder="∞" />
                </div>
                <div>
                    <Label>From <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <div className="relative mt-1">
                        <Input type="date" data-testid="filter-from-input" className="rounded-xl h-10 pr-8" value={filters.available_from} onChange={set("available_from")} />
                        {filters.available_from && (
                            <button
                                type="button"
                                data-testid="filter-from-clear"
                                onClick={() => { const next = { ...filters, available_from: "" }; setFilters(next); load(next); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground flex items-center justify-center text-xs leading-none"
                                aria-label="Clear from date"
                                title="Clear"
                            >×</button>
                        )}
                    </div>
                </div>
                <div>
                    <Label>Until <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <div className="relative mt-1">
                        <Input type="date" data-testid="filter-to-input" className="rounded-xl h-10 pr-8" value={filters.available_to} onChange={set("available_to")} />
                        {filters.available_to && (
                            <button
                                type="button"
                                data-testid="filter-to-clear"
                                onClick={() => { const next = { ...filters, available_to: "" }; setFilters(next); load(next); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground flex items-center justify-center text-xs leading-none"
                                aria-label="Clear until date"
                                title="Clear"
                            >×</button>
                        )}
                    </div>
                </div>
                <div className="lg:col-span-6 flex flex-wrap items-center gap-2 justify-between">
                    <p className="text-xs text-muted-foreground">
                        Price filter matches the number regardless of currency.
                    </p>
                    <div className="flex gap-2 justify-end">
                        <Button type="button" variant="ghost" onClick={reset} data-testid="filter-reset-btn" className="rounded-full">Reset</Button>
                        <Button type="submit" data-testid="filter-apply-btn" className="rounded-full bg-primary hover:bg-primary/90">
                            <Search className="w-4 h-4 mr-1" /> Search
                        </Button>
                    </div>
                </div>
            </form>

            <div className="mt-8">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="aspect-[4/3] bg-muted rounded-2xl animate-pulse" />
                        ))}
                    </div>
                ) : rooms.length === 0 ? (
                    <div className="border border-dashed border-border rounded-2xl p-12 text-center" data-testid="browse-empty">
                        <Filter className="w-10 h-10 mx-auto text-muted-foreground" />
                        <p className="mt-3 text-muted-foreground">No rooms found. Try adjusting your filters.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="browse-results">
                            {visibleRooms.map((r, i) => (
                                <motion.div
                                    key={r.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4), ease: "easeOut" }}
                                >
                                    <RoomCard room={r} />
                                </motion.div>
                            ))}
                        </div>

                        {showLockedSection && (
                            <LockedSection
                                user={user}
                                filterActive={showLockedOnly}
                                teaserRooms={teaserRooms}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
        </PullToRefresh>
    );
}

function LockedSection({ user, filterActive, teaserRooms }) {
    const loggedIn = !!user;
    const eyebrow = filterActive ? "Search is locked" : "More rooms inside";
    const headline = filterActive
        ? <>List your room to <span className="text-primary">search the rest</span>.</>
        : <>List your room to unlock <span className="text-primary">every room worldwide</span>.</>;
    const body = filterActive
        ? "Filters work for members who have a room listed. Add yours and you can search every listing, message any owner, and start matching today."
        : "ErasmusSwap works because everyone is hosting a room. Add yours and you'll see every other listing, message any owner, and start matching today.";

    return (
        <section data-testid="browse-locked-section" className="mt-12 relative">
            {/* Blurred teaser strip behind the CTA card */}
            {teaserRooms.length > 0 && (
                <div
                    aria-hidden="true"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pointer-events-none select-none"
                    style={{ filter: "blur(14px)", opacity: 0.55 }}
                >
                    {teaserRooms.map((r) => <RoomCard key={r.id} room={r} />)}
                </div>
            )}

            {/* Overlay CTA card */}
            <div className={teaserRooms.length > 0 ? "absolute inset-0 flex items-center justify-center px-4" : "flex items-center justify-center px-4"}>
                <div className="relative max-w-xl w-full bg-white border border-border rounded-3xl shadow-xl p-8 md:p-10 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary mx-auto flex items-center justify-center">
                        <Lock className="w-6 h-6" strokeWidth={2.2} />
                    </div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mt-4">
                        {eyebrow}
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 leading-tight">
                        {headline}
                    </h2>
                    <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                        {body}
                    </p>

                    <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 justify-center">
                        {loggedIn ? (
                            <Link
                                to="/listings/new"
                                data-testid="locked-cta-list"
                                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 py-3 font-semibold transition-colors"
                            >
                                <Plus className="w-4 h-4" /> Add my room
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/register"
                                    data-testid="locked-cta-register"
                                    className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 py-3 font-semibold transition-colors"
                                >
                                    Sign up and list <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/login"
                                    data-testid="locked-cta-login"
                                    className="inline-flex items-center justify-center text-sm text-foreground/80 hover:text-foreground"
                                >
                                    or log in
                                </Link>
                            </>
                        )}
                    </div>

                    <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> 100% free</span>
                        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-secondary" /> No commission</span>
                        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-foreground" /> Delete any time</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
